import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import { orderKeys } from "../../data/query-keys/orders";
import { ApiError } from "../../shared/api/client";
import type {
  ChangeStatusAcknowledgement,
  OrderDetails,
  OrderSnapshotResponse,
  OrderSummary,
} from "../../shared/api/types";
import {
  db,
  getOrCreateClientId,
  type QueuedCommand,
} from "../../storage/indexed-db/database";
import { recordDuration } from "../../shared/utilities/performance";
import { sendStatusCommand } from "../../features/orders/api/ordersApi";

const inFlightEntities = new Set<number>();

function updateSummary(
  value: InfiniteData<OrderSnapshotResponse> | undefined,
  orderId: number,
  updater: (order: OrderSummary) => OrderSummary,
): InfiniteData<OrderSnapshotResponse> | undefined {
  if (!value) return value;
  return {
    ...value,
    pages: value.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        item.summary.id === orderId
          ? { ...item, summary: updater(item.summary) }
          : item,
      ),
    })),
  };
}

export function updateOrderCaches(
  queryClient: QueryClient,
  orderId: number,
  updater: (order: OrderSummary) => OrderSummary,
  detailUpdater?: (order: OrderDetails) => OrderDetails,
): void {
  queryClient.setQueriesData<InfiniteData<OrderSnapshotResponse>>(
    { queryKey: orderKeys.lists() },
    (value) => updateSummary(value, orderId, updater),
  );
  queryClient.setQueryData<OrderDetails>(
    orderKeys.detail(orderId),
    (value) => (value && detailUpdater ? detailUpdater(value) : value),
  );
}

function applyAcknowledgement(
  queryClient: QueryClient,
  acknowledgement: ChangeStatusAcknowledgement,
): void {
  updateOrderCaches(
    queryClient,
    acknowledgement.entityId,
    (order) => ({
      ...order,
      status: acknowledgement.canonicalStatus,
      entityVersion: acknowledgement.entityVersion,
      syncState: undefined,
    }),
    (order) => ({
      ...order,
      status: acknowledgement.canonicalStatus,
      entityVersion: acknowledgement.entityVersion,
      syncState: undefined,
    }),
  );
}

function rollbackCommand(queryClient: QueryClient, command: QueuedCommand): void {
  updateOrderCaches(
    queryClient,
    command.entityId,
    (order) => ({
      ...order,
      status: command.rollback.status,
      entityVersion: command.rollback.entityVersion,
      syncState: "failed",
    }),
    (order) => ({
      ...order,
      status: command.rollback.status,
      entityVersion: command.rollback.entityVersion,
      syncState: "failed",
    }),
  );
}

export async function enqueueOrderStatus(
  queryClient: QueryClient,
  order: OrderSummary | OrderDetails,
  newStatus: string,
): Promise<string> {
  const started = performance.now();
  const clientMutationId = crypto.randomUUID();
  const clientId = await getOrCreateClientId();
  const command: QueuedCommand = {
    clientMutationId,
    clientId,
    commandType: "order.changeStatus",
    entityType: "order",
    entityId: order.id,
    expectedVersion: order.entityVersion,
    payload: { newStatus },
    rollback: {
      status: order.status,
      entityVersion: order.entityVersion,
    },
    createdAt: new Date().toISOString(),
    nextAttemptAt: Date.now(),
    attemptCount: 0,
    status: "queued",
  };

  updateOrderCaches(
    queryClient,
    order.id,
    (current) => ({ ...current, status: newStatus, syncState: "pending" }),
    (current) => ({ ...current, status: newStatus, syncState: "pending" }),
  );
  await db.commands.put(command);
  recordDuration("erp.order-status.optimistic", started);
  void flushOrderCommands(queryClient);
  return clientMutationId;
}

function conflictCanonical(error: ApiError):
  | { status: string; entityVersion: number }
  | undefined {
  if (error.status !== 409 || !error.body || typeof error.body !== "object") {
    return undefined;
  }
  const nested = (error.body as { error?: { canonical?: unknown } }).error
    ?.canonical;
  if (!nested || typeof nested !== "object") return undefined;
  const value = nested as { status?: unknown; entityVersion?: unknown };
  if (
    typeof value.status !== "string" ||
    typeof value.entityVersion !== "number"
  ) {
    return undefined;
  }
  return { status: value.status, entityVersion: value.entityVersion };
}

async function processCommand(
  queryClient: QueryClient,
  command: QueuedCommand,
): Promise<void> {
  if (inFlightEntities.has(command.entityId)) return;
  inFlightEntities.add(command.entityId);
  const started = performance.now();
  await db.commands.update(command.clientMutationId, {
    status: "sending",
    attemptCount: command.attemptCount + 1,
  });

  try {
    const acknowledgement = await sendStatusCommand(
      command.entityId,
      command.payload.newStatus,
      command.expectedVersion,
      command.clientMutationId,
      command.clientId,
    );
    applyAcknowledgement(queryClient, acknowledgement);
    await db.commands.update(command.clientMutationId, {
      status: "acknowledged",
      error: undefined,
    });
    recordDuration("erp.order-status.ack", started);
  } catch (error) {
    if (error instanceof ApiError && error.retryable) {
      const attempt = command.attemptCount + 1;
      const backoff = Math.min(60_000, 1_000 * 2 ** Math.min(attempt, 6));
      const jitter = Math.floor(Math.random() * 500);
      await db.commands.update(command.clientMutationId, {
        status: "queued",
        attemptCount: attempt,
        nextAttemptAt: Date.now() + backoff + jitter,
        error: error.message,
      });
      updateOrderCaches(
        queryClient,
        command.entityId,
        (order) => ({ ...order, syncState: "retrying" }),
        (order) => ({ ...order, syncState: "retrying" }),
      );
      return;
    }

    const canonical = error instanceof ApiError ? conflictCanonical(error) : undefined;
    if (canonical) {
      updateOrderCaches(
        queryClient,
        command.entityId,
        (order) => ({
          ...order,
          status: canonical.status,
          entityVersion: canonical.entityVersion,
          syncState: "conflict",
        }),
        (order) => ({
          ...order,
          status: canonical.status,
          entityVersion: canonical.entityVersion,
          syncState: "conflict",
        }),
      );
      await db.commands.update(command.clientMutationId, {
        status: "conflict",
        error: error instanceof Error ? error.message : "Verzióütközés",
      });
      return;
    }

    rollbackCommand(queryClient, command);
    await db.commands.update(command.clientMutationId, {
      status: "failed",
      error: error instanceof Error ? error.message : "A command sikertelen",
    });
  } finally {
    inFlightEntities.delete(command.entityId);
  }
}

export async function flushOrderCommands(queryClient: QueryClient): Promise<void> {
  if (!navigator.onLine) return;
  const commands = await db.commands
    .where("status")
    .anyOf(["queued", "sending"])
    .sortBy("createdAt");
  const firstByEntity = new Map<number, QueuedCommand>();
  for (const command of commands) {
    if (
      command.nextAttemptAt <= Date.now() &&
      !firstByEntity.has(command.entityId)
    ) {
      firstByEntity.set(command.entityId, command);
    }
  }
  await Promise.all(
    [...firstByEntity.values()].map((command) =>
      processCommand(queryClient, command),
    ),
  );
}

export async function confirmCommand(
  clientMutationId: string,
): Promise<void> {
  await db.commands.update(clientMutationId, { status: "confirmed" });
}
